import React, { useState } from 'react';
import { contractAdjustmentsService } from '../../services/api';
import { fmtDate, getTodayStr } from '../../utils/formatters';
import { TONE_BADGE, TONE_TEXT } from '../../utils/statusColors';
import { campo, mensagemDoErroDeCarga } from '../../design';
import {
  CANAIS_DO_EVENTO, corpoDoRegistro, DirecaoDoRegistro, marcadorDoUltimoContato, problemaDoRegistro, UltimoContato,
} from '../../utils/ultimoContato';

/**
 * Espelho da tratativa em Reajustes (contrato do backend, §10; prévia https://claude.ai/artifact/1QFXWSCRyPKqkgiGmE3dRa;
 * decisões do Heitor de 31/10/2026: Q1 C1 S1 M1).
 *
 * - `MarcadorDoContato`: "último contato: canal · dd/mm · há N dias", vermelho só com mais de 30 dias sem resposta. Vai
 *   na coluna Estado, abaixo dos selos (M1).
 * - `RegistrarContato`: a caixinha curta, aberta na própria linha (Q1). WhatsApp já marcado (C1); "falei com eles" ou
 *   "eles responderam" obrigatório, sem padrão (S1), porque é o que decide o vermelho.
 *
 * Grava pelas rotas que já existiam: a da anualidade e, para contrato sem anualidade, a do contrato.
 */

const fantasma = 'px-2.5 py-1 rounded-lg text-xs font-medium whitespace-nowrap border border-central-borda bg-central-cartao text-central-texto hover:bg-central-elevado disabled:opacity-40';

export function MarcadorDoContato({ ultimoContato }: { ultimoContato: UltimoContato | null | undefined }) {
  const m = marcadorDoUltimoContato(ultimoContato);
  return (
    <p
      className={`text-[11px] mt-1 ${m.vermelho ? TONE_TEXT.danger : 'text-slate-500'}`}
      data-ultimo-contato={m.vermelho ? 'vermelho' : m.semContato ? 'nenhum' : 'neutro'}
    >
      {m.texto}
    </p>
  );
}

export interface AlvoDoContato {
  /** A anualidade, quando há. Sem ela, o registro vai para a linha do tempo do CONTRATO. */
  adjustmentId?: number | null;
  contractId: number;
  cliente: string;
}

export default function RegistrarContato({ alvo, onRegistrado, onClose }: {
  alvo: AlvoDoContato;
  onRegistrado: () => void;
  onClose: () => void;
}) {
  const [canal, setCanal] = useState<string>('whatsapp');
  const [direcao, setDirecao] = useState<DirecaoDoRegistro | null>(null);
  const [texto, setTexto] = useState('');
  const [data, setData] = useState(getTodayStr());
  const [quem, setQuem] = useState('');
  const [mais, setMais] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const registro = { canal, direcao, texto, data, quem };
  const problema = problemaDoRegistro(registro);
  const hoje = getTodayStr();

  const salvar = async () => {
    if (problema || salvando) return;
    setSalvando(true);
    setErro(null);
    try {
      const corpo = corpoDoRegistro(registro);
      if (alvo.adjustmentId) await contractAdjustmentsService.addEvent(alvo.adjustmentId, corpo);
      else await contractAdjustmentsService.addContractEvent(alvo.contractId, corpo);
      onRegistrado();
    } catch (e: any) {
      const msg = e?.response?.data?.message;
      setErro(Array.isArray(msg) ? msg.join('; ') : msg || mensagemDoErroDeCarga(e));
      setSalvando(false);
    }
  };

  return (
    <div
      role='form'
      aria-label={`Registrar contato com ${alvo.cliente}`}
      className='rounded-xl border border-blue-500/60 bg-central-cartao p-3 space-y-3 max-w-xl'
      data-registrar-contato
      onKeyDown={(e) => { if (e.key === 'Escape') onClose(); }}
    >
      <div className='flex items-baseline justify-between gap-2'>
        <p className='text-sm font-semibold text-central-texto'>Registrei contato · {alvo.cliente}</p>
        <button type='button' aria-label='Fechar' className='text-central-texto-fraco hover:text-central-texto' onClick={onClose}>×</button>
      </div>

      <fieldset>
        <legend className='text-[11px] text-central-texto-fraco mb-1'>Canal</legend>
        <div className='flex flex-wrap gap-1.5'>
          {CANAIS_DO_EVENTO.filter((c) => c.value !== 'sistema').map((c) => (
            <label key={c.value} className={`px-2.5 py-0.5 rounded-full text-xs border cursor-pointer ${canal === c.value ? 'border-blue-500 text-central-texto bg-blue-500/10' : 'border-central-borda text-central-texto-fraco'}`}>
              <input type='radio' name='canal-do-contato' value={c.value} checked={canal === c.value} onChange={() => setCanal(c.value)} className='sr-only' />
              {c.label}
            </label>
          ))}
        </div>
      </fieldset>

      <fieldset>
        <legend className='text-[11px] text-central-texto-fraco mb-1'>O que foi</legend>
        <div className='flex flex-wrap gap-4 text-sm text-central-texto'>
          <label className='inline-flex items-center gap-1.5 cursor-pointer'>
            <input type='radio' name='direcao-do-contato' checked={direcao === 'enviado'} onChange={() => setDirecao('enviado')} />
            falei com eles
          </label>
          <label className='inline-flex items-center gap-1.5 cursor-pointer'>
            <input type='radio' name='direcao-do-contato' checked={direcao === 'recebido'} onChange={() => setDirecao('recebido')} />
            eles responderam
          </label>
        </div>
      </fieldset>

      <div>
        <label className='block text-[11px] text-central-texto-fraco mb-1' htmlFor={`contato-texto-${alvo.contractId}-${alvo.adjustmentId || 0}`}>O que aconteceu</label>
        <textarea
          id={`contato-texto-${alvo.contractId}-${alvo.adjustmentId || 0}`}
          value={texto}
          onChange={(e) => setTexto(e.target.value.slice(0, 500))}
          rows={2}
          placeholder='Ex.: falei com a gestora, vai levar ao jurídico esta semana.'
          className={`w-full px-3 py-2 rounded-lg text-sm ${campo}`}
        />
        <p className='text-[11px] text-central-texto-fraco mt-1'>
          de 5 a 500 letras · {data === hoje ? 'hoje' : fmtDate(data)}
          {!mais && <> · <button type='button' className='underline hover:text-central-texto' onClick={() => setMais(true)}>outra data ou quem, do lado de lá</button></>}
        </p>
      </div>

      {mais && (
        <div className='grid gap-2 sm:grid-cols-2'>
          <label className='block text-[11px] text-central-texto-fraco'>Quando
            <input type='date' max={hoje} value={data} onChange={(e) => setData(e.target.value)} className={`mt-1 block w-full px-2 py-1.5 rounded-lg text-sm ${campo}`} />
          </label>
          <label className='block text-[11px] text-central-texto-fraco'>Quem, do lado de lá
            <input type='text' value={quem} onChange={(e) => setQuem(e.target.value.slice(0, 200))} placeholder='Ana Zanoni' className={`mt-1 block w-full px-2 py-1.5 rounded-lg text-sm ${campo}`} />
          </label>
        </div>
      )}

      {erro && <p role='alert' className={`px-3 py-2 rounded-lg text-sm ${TONE_BADGE.danger}`}>Não registrou: {erro}</p>}

      <div className='flex items-center justify-end gap-2'>
        {problema && texto.trim().length > 0 && <span className='text-[11px] text-central-texto-fraco mr-auto' data-falta>{problema}</span>}
        <button type='button' className={fantasma} onClick={onClose}>Cancelar</button>
        <button type='button' className={fantasma} disabled={!!problema || salvando} onClick={salvar}>{salvando ? 'Registrando…' : 'Registrar'}</button>
      </div>
    </div>
  );
}
